import React, { useState, useEffect } from 'react';
import { Input } from '../../ui/input';
import { secondsToMMSS, mmssToSeconds } from '../../utils/timeFormat';

export function InlineEditTime({ 
  value, 
  minSeconds = 10, 
  maxSeconds = 3600, 
  onSave,
  useDefault = false,
  defaultTime = 0
}: { 
  value: number | null | undefined; 
  minSeconds?: number; 
  maxSeconds?: number; 
  onSave: (value: number) => void;
  useDefault?: boolean;
  defaultTime?: number;
}) {
  // Use default if value is 0/null and useDefault is true
  const effectiveValue = useDefault && (value === null || value === undefined || value === 0) 
    ? defaultTime 
    : (value ?? 0);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(secondsToMMSS(effectiveValue));

  useEffect(() => {
    const effective = useDefault && (value === null || value === undefined || value === 0) 
      ? defaultTime 
      : (value ?? 0);
    setEditValue(secondsToMMSS(effective));
  }, [value, useDefault, defaultTime]);

  const handleSave = () => {
    const seconds = mmssToSeconds(editValue);
    if (seconds !== null && seconds >= minSeconds && seconds <= maxSeconds) {
      onSave(seconds);
      setEditing(false);
    } else {
      // Reset to original value if invalid
      const effective = useDefault && (value === null || value === undefined || value === 0) 
        ? defaultTime 
        : (value ?? 0);
      setEditValue(secondsToMMSS(effective));
      setEditing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      const effective = useDefault && (value === null || value === undefined || value === 0) 
        ? defaultTime 
        : (value ?? 0);
      setEditValue(secondsToMMSS(effective));
      setEditing(false);
    }
  };

  const seconds = mmssToSeconds(editValue);
  const isValid = seconds !== null && seconds >= minSeconds && seconds <= maxSeconds;

  if (editing) {
    return (
      <Input
        type="text"
        placeholder="MM:SS"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className={`w-20 h-7 text-sm ${!isValid ? 'border-destructive-action focus-visible:ring-destructive-action focus-visible:ring-2' : ''}`}
        autoFocus
        pattern="[0-9]{1,2}:[0-5][0-9]"
      />
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="px-2 py-1 hover:bg-gray-100 rounded text-sm"
    >
      {secondsToMMSS(effectiveValue)}
    </button>
  );
}

